<script setup lang="ts">
/**
 * Feedback for the gap between tapping something like Deposit and the modal
 * appearing.
 *
 * Since #819 the deposit/withdraw modals are mounted on first open rather than
 * at page load, which keeps the LiFi widget's React stack out of the startup
 * bundle. The cost is that the first tap now fetches the modal's own chunk
 * (~105 kB), and that fetch queues behind whatever the game is still streaming.
 * Measured on a throttled mobile profile: 361 ms on a fast connection, 1.2 s on
 * 4G, and up to ~21 s on Slow 4G with the game's assets competing for the pipe.
 *
 * Without this the button simply looks dead for that whole window. `delay` on
 * the async component keeps the overlay from flashing when the chunk is already
 * cached, which is every open after the first.
 *
 * The loading state does NOT capture input. It sits over a live betting page,
 * and on the Slow-4G tail it would otherwise cover the cash-out button for
 * twenty seconds of a running round — opening Deposit mid-round must never cost
 * someone their bet. The error state does block, because it needs a decision,
 * but it is dismissible by button, by backdrop and by Escape.
 *
 * It is an `alert`, not a modal `dialog`, and deliberately does not trap focus:
 * two buttons plus Escape did not seem worth a focus cycle on an error path
 * whose whole job is to stay dependency-free. The cost is that a second Tab
 * walks a keyboard user onto the page behind the backdrop; Escape brings them
 * back. Revisit if the card ever grows more controls.
 *
 * **Reload is the only real recovery, which is why it is the primary action.**
 * A failed module fetch is recorded in the document's module map, so re-running
 * the import — however the app dresses it up — is rejected without a network
 * request (#845, evidence in `utils/asyncModalStates`). Closing the card leaves
 * the user on a page where Deposit will keep failing; that is honest, and it is
 * still offered, because forcing a reload on someone mid-round would be worse.
 *
 * Deliberately dependency-free apart from Vue itself — anything else it
 * imported would land in the eager graph and eat into the saving it exists to
 * protect. Note also that the root element must stay a single node with no
 * sibling comment: a fragment root would silently drop the class and the ARIA
 * attributes.
 */
import { onMounted, onBeforeUnmount, useTemplateRef } from 'vue'

const { t } = useI18n()

const props = defineProps<{ failed?: boolean }>()
const emit = defineEmits<{ close: [] }>()

const reloadButton = useTemplateRef<HTMLButtonElement>('reloadButton')

function close(): void {
  emit('close')
}

function reload(): void {
  window.location.reload()
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') close()
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  // The error state is the only branch that takes focus — it is a dialog the
  // user has to act on, and without this their focus stays somewhere behind the
  // backdrop they can no longer see.
  if (props.failed) reloadButton.value?.focus()
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div
    class="fixed inset-0 z-[100] flex items-center justify-center"
    :class="failed ? 'bg-black/70' : 'pointer-events-none'"
    :role="failed ? 'alert' : 'status'"
    :aria-live="failed ? 'assertive' : 'polite'"
    @click="failed && close()"
  >
    <!--
      The dark disc is not decoration: with no backdrop, a white ring over the
      game's bright winter scene is almost invisible, which defeats the point of
      showing a pending state at all. It is small enough not to read as a modal.
    -->
    <div v-if="!failed" class="rounded-full bg-black/55 p-3">
      <div class="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white" />
    </div>
    <div
      v-else
      class="mx-6 max-w-xs rounded-xl bg-[#1a2133] px-5 py-4 text-center"
      @click.stop
    >
      <p class="text-sm text-white/85">{{ t('errors.chunkLoadFailed') }}</p>
      <button
        ref="reloadButton"
        type="button"
        class="mt-4 w-full rounded-lg bg-white/15 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/25"
        @click="reload"
      >
        {{ t('errors.chunkLoadReload') }}
      </button>
      <button
        type="button"
        class="mt-2 w-full rounded-lg px-4 py-2 text-sm text-white/60 transition-colors hover:text-white/85"
        @click="close"
      >
        {{ t('errors.chunkLoadDismiss') }}
      </button>
    </div>
  </div>
</template>
